import { useState, type FormEvent } from "react";
import {
  normalizeUsername,
  USERNAME_MAX_LENGTH,
  USERNAME_MIN_LENGTH,
  validateUsername,
} from "@fortress/shared";
import { setUsername } from "./api";
import { saveAccessSession, withAuthRetry } from "./session";

// Shown after login when the account has no username yet. The handle must be
// unique, lowercase, and use only letters/digits with `.` / `-` as separators.

function returnTo(): string {
  const param = new URLSearchParams(window.location.search).get("returnTo");
  return param && param.startsWith("/") && !param.startsWith("//") ? param : "/";
}

function usernameErrorMessage(code: string): string {
  switch (code) {
    case "empty":
      return "Enter a username.";
    case "too_short":
      return `Use at least ${USERNAME_MIN_LENGTH} characters.`;
    case "too_long":
      return `Use at most ${USERNAME_MAX_LENGTH} characters.`;
    case "invalid":
      return "Use lowercase letters and numbers. . and - are allowed between characters, not at the ends, and not repeated.";
    case "taken":
      return "That username is taken. Try another.";
    case "already_set":
      return "Your username is already set.";
    default:
      return "Couldn't save that username. Try again.";
  }
}

export function ChooseUsernamePage() {
  const [username, setUsernameInput] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    const normalized = normalizeUsername(username);
    const validation = validateUsername(normalized);
    if (validation) {
      setError(usernameErrorMessage(validation));
      return;
    }

    setBusy(true);
    setError(null);
    try {
      const session = await withAuthRetry((accessToken) =>
        setUsername(normalized, accessToken),
      );
      await saveAccessSession(session);
      window.location.replace(returnTo());
    } catch (err) {
      if (err instanceof Error && err.message === "not_authenticated") {
        window.location.replace(`/auth?returnTo=${encodeURIComponent(returnTo())}`);
        return;
      }
      setError(usernameErrorMessage(err instanceof Error ? err.message : ""));
      setBusy(false);
    }
  }

  return (
    <div className="auth-page">
      <div className="auth-card">
        <h1 className="auth-title">Choose a username</h1>
        <p className="auth-subtitle">
          Pick a unique handle. Lowercase only — letters, numbers, and . or -
          between characters.
        </p>

        <form className="auth-email" onSubmit={onSubmit}>
          <label className="auth-label" htmlFor="auth-username">
            Username
          </label>
          <input
            id="auth-username"
            className="auth-input"
            type="text"
            autoComplete="username"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            placeholder="e.g. fortress.ace"
            value={username}
            onChange={(e) =>
              setUsernameInput(
                e.target.value.toLowerCase().replace(/[^a-z0-9.-]/g, ""),
              )
            }
            maxLength={USERNAME_MAX_LENGTH}
            disabled={busy}
            required
            autoFocus
          />
          <button className="auth-submit" type="submit" disabled={busy}>
            {busy ? "Saving…" : "Continue"}
          </button>
        </form>

        {error && <p className="auth-error">{error}</p>}
      </div>
    </div>
  );
}
